/**
 * Chiamate all'API.
 *
 * Due cose che valgono per tutte:
 *  - `credentials: 'same-origin'`, perché la sessione è un cookie host-only;
 *  - l'errore che arriva è una **chiave di traduzione**, non un testo: qui non
 *    si costruiscono messaggi, si passa la chiave a chi la mostra.
 */

export type ApiErrorBody = {
  error: { code: string; messageKey: string; details?: Record<string, string | number | boolean> };
};

export class ApiError extends Error {
  readonly code: string;
  readonly messageKey: string;
  readonly details: Record<string, string | number | boolean> | undefined;
  readonly status: number;

  constructor(status: number, body: ApiErrorBody['error']) {
    super(body.code);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.messageKey = body.messageKey;
    this.details = body.details;
  }
}

/**
 * Il nome esatto del cookie CSRF dello staff, iniettato dalla build.
 *
 * **Non** per suffisso: sullo stesso host vive anche il cookie della pagina di
 * video e chat, e un volontario che è pure utente registrato — la sezione
 * 5.5bis lo prevede — vedeva la console mandare il token sbagliato e ricevere
 * 403 sul pulsante di stato.
 */
declare const __CSRF_COOKIE__: string | undefined;

const CSRF_COOKIE_NAME =
  typeof __CSRF_COOKIE__ === 'string' && __CSRF_COOKIE__ ? __CSRF_COOKIE__ : 'sid-csrf';

function csrfToken(): string | undefined {
  const cookies = globalThis.document?.cookie ?? '';
  for (const entry of cookies.split(';')) {
    const [name, value] = entry.split('=').map((part) => part.trim());
    if (name === CSRF_COOKIE_NAME && value) return decodeURIComponent(value);
  }
  return undefined;
}

/**
 * Opzioni di una lettura.
 *
 * `signal` c'è su tutte le letture che una schermata rifà al cambio di un
 * filtro: due richieste concorrenti possono tornare invertite, e la seconda a
 * partire deve poter annullare la prima invece di lasciarla vincere
 * (`useLoad`, `docs/DECISIONI.md`, AK1).
 */
export type ReadOptions = { signal?: AbortSignal };

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = csrfToken();
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...(token ? { 'x-csrf-token': token } : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => undefined)) as ApiErrorBody | undefined;
    throw new ApiError(
      response.status,
      body?.error ?? { code: 'unexpected', messageKey: 'errors.unexpected' },
    );
  }

  return (await response.json()) as T;
}

export type LoginResult =
  | {
      status: 'ok';
      csrfToken: string;
      user: {
        id: string;
        kind: string;
        displayName: string;
        role: string;
        preferredLocale: string;
      };
    }
  | { status: 'totp_required'; challengeToken: string }
  | { status: 'totp_setup_required'; challengeToken: string; secret: string; otpauthUri: string };

export function login(input: { email: string; password: string }): Promise<LoginResult> {
  return request('/api/staff/login', { method: 'POST', body: JSON.stringify(input) });
}

export function verifyTotp(input: { challengeToken: string; code: string }): Promise<LoginResult> {
  return request('/api/staff/login/totp', { method: 'POST', body: JSON.stringify(input) });
}

export function enrollTotp(input: {
  challengeToken: string;
  code: string;
  secret: string;
}): Promise<LoginResult> {
  return request('/api/staff/login/totp/setup', { method: 'POST', body: JSON.stringify(input) });
}

/**
 * Password dimenticata (mock 2, `st-forgot`).
 *
 * La risposta è sempre `{ status: 'ok' }`, che l'indirizzo esista o no (G5):
 * questa funzione non ha modo di dire a chi la chiama se l'email è partita
 * davvero, ed è voluto.
 */
export function requestPasswordReset(input: { email: string }): Promise<{ status: string }> {
  return request('/api/staff/password-reset/request', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function confirmPasswordReset(input: {
  token: string;
  password: string;
}): Promise<{ status: string }> {
  return request('/api/staff/password-reset/confirm', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export type CurrentUser = {
  id: string;
  kind: string;
  displayName: string;
  email: string;
  role: string;
  preferredLocale: string | null;
  tenant: { slug: string; name: string; timezone: string } | null;
  impersonation: boolean;
};

export function currentUser(): Promise<CurrentUser> {
  return request('/api/staff/me');
}

/**
 * La lingua scelta, ricordata anche **sul profilo**.
 *
 * La scelta vale comunque su questo dispositivo, e lì vince (AQ2). Questa
 * chiamata serve al dispositivo **successivo**: senza, chi cambiava lingua sul
 * telefono la ritrovava diversa sul computer di sede, e le notifiche push —
 * che il server scrive proprio da `preferredLocale` — arrivavano in una lingua
 * diversa da quella della console (AQ8, AS6).
 */
export function setPreferredLocale(locale: string): Promise<{ preferredLocale: string }> {
  return request('/api/staff/me/locale', {
    method: 'PUT',
    body: JSON.stringify({ locale }),
  });
}

export function logout(): Promise<{ status: string }> {
  return request('/api/staff/logout', { method: 'POST' });
}

/**
 * Cambio password da autenticato, per chi è già dentro. Nessuno la impone a
 * un altro (`InvitePage.tsx`): questa è sempre la stessa persona che
 * conferma la password che ha già usato per entrare.
 *
 * Il server riemette la sessione e il cookie CSRF nella stessa risposta — il
 * cambio password invalida ogni sessione più vecchia di questo istante,
 * questa compresa — ma qui non c'è niente da fare con quel dato: `request()`
 * rilegge il cookie CSRF a ogni chiamata, quindi la prossima richiesta lo
 * trova già aggiornato da sola.
 */
export function changePassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ status: string }> {
  return request('/api/staff/me/password', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export type TeamMember = {
  id: string;
  displayName: string;
  name?: string;
  role: string;
  isActive: boolean;
};

export function team(): Promise<{ members: TeamMember[] }> {
  return request('/api/staff/team');
}

/**
 * L'onboarding guidato in cinque passi (mock 5, `o-step1`…`o-step5`).
 *
 * Non porta dati propri: ogni passo riflette una schermata esistente
 * (orari, protocollo, squadra, numeri di emergenza) o, per "installazione",
 * l'unica cosa che il coordinatore può solo dichiarare.
 */
export type OnboardingStepId = 'tenant' | 'hours' | 'protocol' | 'volunteers' | 'installation';

export type OnboardingStep = { id: OnboardingStepId; done: boolean };

export type OnboardingStatus = {
  steps: OnboardingStep[];
  currentStep: OnboardingStepId | undefined;
  complete: boolean;
  installation: { siteKey: string; scriptSrc: string };
};

export function onboardingStatus(): Promise<OnboardingStatus> {
  return request('/api/admin/onboarding');
}

export type OnboardingProgress = {
  steps: OnboardingStep[];
  currentStep: OnboardingStepId | undefined;
  complete: boolean;
};

export function confirmOnboardingInstallation(confirmed: boolean): Promise<OnboardingProgress> {
  return request('/api/admin/onboarding/installation', {
    method: 'POST',
    body: JSON.stringify({ confirmed }),
  });
}

export type ServiceWindow = {
  timezone: string;
  isOpen: boolean;
  opensAt: string | null;
  closesAt: string | null;
};

export function serviceWindow(): Promise<ServiceWindow> {
  return request('/api/staff/service-window');
}

/**
 * Console del volontario.
 *
 * Tutto ciò che riguarda il turno, lo stato e la chiamata in arrivo. Nessuna di
 * queste risposte contiene un dato dell'utente finale: su chiamata anonima non
 * c'è niente da mostrare oltre al fatto che qualcuno sta chiamando (sezione 5.6).
 */

export type PresenceStatus = 'available' | 'busy' | 'paused' | 'offline';

export type ConsoleState = {
  presence: PresenceStatus;
  shift: { startsAt: string; endsAt: string; channels: string[] } | null;
  nextShift: { startsAt: string; endsAt: string } | null;
  service: { isOpen: boolean; opensAt: string | null };
  colleagues: string[];
  sessionsToday: number;
  waiting: number;
  activeSessionId: string | null;
  emergency: { number: string; labelKey: string | null; label: string | null; fixed: boolean }[];
  /**
   * Le registrazioni a video e chat sono aperte?
   *
   * Serve a chi è in turno adesso: se sono ferme, la persona che scrive "non
   * riesco a registrarmi" non sta sbagliando il numero (sezione 5.2).
   */
  registrations: { open: boolean; reason: 'suspended' | 'cap' | null };
  /**
   * Le nuove sessioni sono sospese per una questione amministrativa.
   *
   * Riguarda chi è in turno adesso: senza, si resta "in ascolto" mentre nessuna
   * chiamata può arrivare. Quelle in corso non sono toccate (vincolo 5).
   */
  newSessionsSuspended: boolean;
  /**
   * Proprie conversazioni concluse senza un debriefing, degli ultimi giorni.
   *
   * Sopravvive a un riavvio del browser: la console lo usa per rimettere in
   * coda una nota persa a un crash, non solo quelle viste dal vivo in questa
   * scheda (`ConsolePage.tsx`).
   */
  pendingNotes: { sessionId: string; endedAt: string; channel: string }[];
};

export function consoleState(): Promise<ConsoleState> {
  return request('/api/staff/console');
}

export function setPresence(status: PresenceStatus): Promise<{ status: PresenceStatus }> {
  return request('/api/staff/presence', { method: 'PUT', body: JSON.stringify({ status }) });
}

export function startShift(): Promise<{
  shift: { startsAt: string; endsAt: string };
  presence: PresenceStatus;
}> {
  return request('/api/staff/shift/start', { method: 'POST' });
}

export function endShift(): Promise<{ presence: PresenceStatus }> {
  return request('/api/staff/shift/end', { method: 'POST' });
}

export type IncomingCall = {
  sessionId: string;
  channel: string;
  expiresAt: string;
  anonymous: boolean;
};

export type ActiveCall = { sessionId: string; channel: string; startedAt: string | null };

export function incomingCalls(): Promise<{
  incoming: IncomingCall | null;
  active: ActiveCall | null;
  interrupted: string | null;
}> {
  return request('/api/staff/calls/incoming');
}

export type MediaGrant = { url: string; token: string; roomName: string };

export function acceptCall(sessionId: string): Promise<{
  status: 'accepted' | 'taken' | 'gone';
  sessionId?: string;
  media?: MediaGrant;
}> {
  return request(`/api/staff/sessions/${sessionId}/accept`, { method: 'POST' });
}

export function declineCall(sessionId: string): Promise<{ status: string }> {
  return request(`/api/staff/sessions/${sessionId}/decline`, { method: 'POST' });
}

export function endCall(sessionId: string): Promise<{ status: string }> {
  return request(`/api/staff/sessions/${sessionId}/end`, { method: 'POST' });
}

/** Token media per rientrare in una sessione dopo un ricaricamento della pagina. */
/**
 * Con chi si sta parlando, e cosa ci si è detti prima.
 *
 * Su chiamata anonima è **vuoto**, e non è una mancanza: non esiste niente da
 * collegare (sezione 5.6). Su un canale con account c'è l'etichetta e la
 * conversazione entro novanta giorni — mai il numero di telefono, nemmeno
 * mascherato.
 */
export type SessionContext = {
  anonymous: boolean;
  label: { custom: string | null; number: number } | null;
  previousMessages: ChatMessage[];
  previousNotes: unknown[];
};

export type ChatMessage = {
  id: string;
  sender: 'app_user' | 'volunteer';
  body: string;
  sentAt: string;
};

export function sessionContext(sessionId: string): Promise<SessionContext> {
  return request(`/api/staff/sessions/${sessionId}/context`);
}

export function sessionMessages(
  sessionId: string,
  after?: string,
): Promise<{ messages: ChatMessage[]; ended: boolean }> {
  const query = after ? `?after=${encodeURIComponent(after)}` : '';
  return request(`/api/staff/sessions/${sessionId}/messages${query}`);
}

export function sendSessionMessage(
  sessionId: string,
  body: string,
): Promise<{ message: ChatMessage }> {
  return request(`/api/staff/sessions/${sessionId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

export function callMedia(sessionId: string): Promise<{ media: MediaGrant }> {
  return request(`/api/staff/sessions/${sessionId}/media`, { method: 'POST' });
}

/**
 * Turni.
 *
 * Il calendario è fatto di **fasce di apertura dell'ente**, non di caselle
 * inventate dal client: le calcola il server nel fuso dell'ente, perché è lì
 * che vivono gli orari (`docs/DECISIONI.md`, E6).
 */

export type ShiftSummary = {
  id: string;
  startsAt: string;
  endsAt: string;
  channels: string[];
};

export type ShiftSlot = {
  startsAt: string;
  endsAt: string;
  /** Quante persone coprono la fascia. Zero è ciò che si cerca per primo. */
  covered: number;
  volunteers: string[];
  mine: boolean;
  myShifts: ShiftSummary[];
  /** Coperta da un capo all'altro, o solo per una parte delle sue ore. */
  fullyCovered: boolean;
  bookable: boolean;
  inProgress: boolean;
};

export type ShiftCalendar = {
  timezone: string;
  from: string;
  to: string;
  bookableUntil: string;
  channels: string[];
  /** Il passo, in minuti, che `bookShift` accetta per inizio e fine. */
  bookingGranularityMinutes: number;
  slots: ShiftSlot[];
  myShifts: ShiftSummary[];
};

export function shiftCalendar(range?: { from?: string; to?: string }): Promise<ShiftCalendar> {
  const query = new URLSearchParams();
  if (range?.from) query.set('from', range.from);
  if (range?.to) query.set('to', range.to);
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return request(`/api/staff/shifts${suffix}`);
}

export function bookShift(input: {
  startsAt: string;
  endsAt?: string;
}): Promise<{ status: 'booked' | 'already_booked'; shift: ShiftSummary }> {
  return request('/api/staff/shifts', { method: 'POST', body: JSON.stringify(input) });
}

export function cancelShift(id: string): Promise<{ status: string }> {
  return request(`/api/staff/shifts/${id}`, { method: 'DELETE' });
}

/**
 * Orari di apertura.
 *
 * Li legge tutto lo staff, li cambia solo chi coordina: `editable` dice quale
 * dei due casi è, e non è una preferenza dell'interfaccia — è il permesso, che
 * il server applica comunque.
 */

export type OpeningHour = { weekday: number; opensAt: string; closesAt: string };

export type OpeningHours = {
  timezone: string;
  hours: OpeningHour[];
  editable: boolean;
  service: { isOpen: boolean; opensAt: string | null };
};

export function openingHours(): Promise<OpeningHours> {
  return request('/api/admin/opening-hours');
}

export type SaveHoursResult = OpeningHours & {
  /**
   * Quanti turni futuri sono stati accorciati o tolti dalla modifica, e a
   * quante persone è arrivata la notifica. `unreachable` è il numero che conta
   * davvero per chi coordina: sono le persone da avvisare a voce.
   */
  shifts: { adjusted: number; removed: number; notified: number; unreachable: number };
};

export function saveOpeningHours(hours: OpeningHour[]): Promise<SaveHoursResult> {
  return request('/api/admin/opening-hours', {
    method: 'PUT',
    body: JSON.stringify({ hours }),
  });
}

/**
 * Notifiche (fase 4).
 *
 * Riguardano solo lo staff: non esiste una rotta gemella per l'utente finale, e
 * non deve esistere (vincolo 9, sezione 7ter).
 */

export type NotificationSettings = {
  pushConfigured: boolean;
  /** Chiave pubblica VAPID: serve al browser per iscriversi. */
  publicKey: string | null;
  subscribed: boolean;
  subscribedAt: string | null;
  smsConfigured: boolean;
  /** Ultime cifre del proprio numero, quanto basta a riconoscerlo. */
  escalationPhoneHint: string | null;
};

export function notificationSettings(): Promise<NotificationSettings> {
  return request('/api/staff/notifications');
}

export type PushSubscriptionPayload = {
  endpoint: string;
  keys: { p256dh: string; auth: string };
};

export function savePushSubscription(
  subscription: PushSubscriptionPayload,
): Promise<{ subscribed: boolean; subscribedAt: string }> {
  return request('/api/staff/push/subscription', {
    method: 'PUT',
    body: JSON.stringify(subscription),
  });
}

export function deletePushSubscription(): Promise<{ subscribed: boolean }> {
  return request('/api/staff/push/subscription', { method: 'DELETE' });
}

/**
 * "Prova notifica".
 *
 * L'esito è un valore, non un errore: "nessun dispositivo iscritto" è la cosa
 * più comune al primo turno, e ognuno dei tre casi ha un rimedio diverso da
 * dire (sezione 5.7).
 */
export type NotificationTestOutcome =
  | 'delivered'
  | 'no_subscription'
  | 'failed'
  | 'rate_limited'
  | 'not_configured'
  | 'skipped';

export function testNotification(): Promise<{ outcome: NotificationTestOutcome }> {
  return request('/api/staff/notifications/test', { method: 'POST' });
}

/** `null` toglie il numero: è il modo di rinunciare all'SMS. */
export function saveEscalationPhone(
  phone: string | null,
): Promise<{ escalationPhoneHint: string | null }> {
  return request('/api/staff/notifications/phone', {
    method: 'PUT',
    body: JSON.stringify({ phone }),
  });
}

/**
 * Debriefing, supervisione, protocollo di crisi e moderazione (fase 6).
 *
 * Nessuna di queste risposte contiene il numero di telefono di nessuno, e
 * nessuna contiene il contenuto di una conversazione: quello non esiste, non è
 * stato registrato e non è recuperabile (sezione 9quater).
 */

export type RiskLevel = 'low' | 'follow_up' | 'critical';

export type DebriefNote = {
  riskLevel: RiskLevel | null;
  ageBand: string | null;
  topic: string | null;
  text: string | null;
  referredExternally: boolean;
  createdAt: string;
};

export type SessionDebrief = {
  channel: string;
  anonymous: boolean;
  durationSeconds: number | null;
  startedAt: string | null;
  endedAt: string | null;
  note: DebriefNote | null;
  /** Il volontario ha già segnalato questa conversazione. */
  reported: boolean;
};

export function sessionDebrief(sessionId: string): Promise<SessionDebrief> {
  return request(`/api/staff/sessions/${sessionId}/note`);
}

export function saveDebrief(
  sessionId: string,
  input: {
    riskLevel?: RiskLevel | null;
    ageBand?: string | null;
    topic?: string | null;
    text?: string | null;
    referredExternally?: boolean;
  },
): Promise<{ status: 'saved' | 'already_written'; noteId?: string }> {
  return request(`/api/staff/sessions/${sessionId}/note`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Segnalare chi ha chiamato. Su una sessione anonima non porta a un blocco. */
export function reportUser(
  sessionId: string,
  input: { reason: string; text?: string },
): Promise<{ status: 'recorded' | 'already_reported'; bannable: boolean }> {
  return request(`/api/staff/sessions/${sessionId}/report`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export type CrisisProtocol = {
  steps: string[];
  onCallPhone: string | null;
  version: number;
};

export type SupervisionMessage = {
  id: string;
  sender: 'volunteer' | 'supervisor';
  body: string;
  sentAt: string;
};

export type SupervisionThread = {
  sessionId: string;
  supervisionId: string;
  myRole: 'volunteer' | 'supervisor';
  reason: string;
  startedAt: string;
  volunteer: { displayName: string } | null;
  claimed: boolean;
  session: {
    channel: string;
    anonymous: boolean;
    startedAt: string | null;
    live: boolean;
  } | null;
};

export type EscalationResult = {
  supervisionId: string;
  /** `null` quando nessuno era reperibile: è il caso frequente (sezione 5.9). */
  supervisor: { displayName: string } | null;
  alreadyOpen: boolean;
  protocol: CrisisProtocol | null;
  messages: SupervisionMessage[];
};

export function escalateToSupervisor(sessionId: string, reason: string): Promise<EscalationResult> {
  return request(`/api/staff/sessions/${sessionId}/escalate`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export type SupervisionView = SupervisionThread & {
  messages: SupervisionMessage[];
  protocol: CrisisProtocol | null;
};

export function supervisionThread(sessionId: string): Promise<SupervisionView> {
  return request(`/api/staff/supervision/${sessionId}`);
}

export function sendSupervisionMessage(
  sessionId: string,
  body: string,
): Promise<{ message: SupervisionMessage }> {
  return request(`/api/staff/supervision/${sessionId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

export type OnCallWindow = { id: string; startsAt: string; endsAt: string };

export function supervisionThreads(): Promise<{
  threads: SupervisionThread[];
  /** La fascia in corso adesso, se ce n'è una. */
  onCall: { until: string } | null;
  /** Le proprie fasce dichiarate, da qui in avanti. */
  myOnCall: OnCallWindow[];
  /** Chi copre cosa: solo per chi coordina (mock 3). */
  coverage?: { startsAt: string; endsAt: string; displayName: string | null }[];
}> {
  return request('/api/staff/supervision');
}

/**
 * Dichiararsi reperibili.
 *
 * Senza questa, "Allerta supervisore" non troverebbe mai nessuno e il caso
 * "nessun supervisore" — quello che la spec chiede di progettare con più cura —
 * sarebbe l'unico caso esistente (sezione 5.9).
 */
export function bookOnCall(input: {
  startsAt: string;
  endsAt: string;
}): Promise<{ status: string; shift: ShiftSummary }> {
  return request('/api/staff/shifts/on-call', { method: 'POST', body: JSON.stringify(input) });
}

export type ProtocolState = {
  protocol:
    | (CrisisProtocol & {
        id: string;
        /** Tutte le lingue: solo per chi coordina, che le modifica. */
        stepsByLocale?: Record<string, string[]>;
        updatedAt: string;
      })
    | null;
  acknowledged: boolean;
  editable: boolean;
};

export function crisisProtocol(): Promise<ProtocolState> {
  return request('/api/staff/crisis-protocol');
}

export function acknowledgeProtocol(): Promise<{ acknowledged: boolean; version: number }> {
  return request('/api/staff/crisis-protocol/ack', { method: 'POST' });
}

export function saveCrisisProtocol(input: {
  steps: Record<string, string[]>;
  onCallPhone?: string | null;
}): Promise<{
  protocol: { id: string; version: number; steps: Record<string, string[]>; updatedAt: string };
  /** Quante persone dovranno rileggerlo prima del prossimo turno (AC8). */
  acksReset: number;
}> {
  return request('/api/admin/crisis-protocol', { method: 'PUT', body: JSON.stringify(input) });
}

export type ModerationSession = {
  id: string;
  channel: string;
  anonymous: boolean;
  requestedAt: string;
  durationSeconds: number | null;
  outcome: string | null;
} | null;

export type ReportStatus = 'open' | 'reviewing' | 'closed';

/**
 * Il debriefing della sessione segnalata.
 *
 * L'unica traccia di cosa quella conversazione è stata: non esiste
 * registrazione, non esiste trascrizione, non esiste contenuto (AC2).
 */
export type ReportNote = {
  id: string;
  riskLevel: RiskLevel | null;
  ageBand: string | null;
  topic: string | null;
  text: string | null;
  referredExternally: boolean;
  supervisorAlerted: boolean;
  reviewedAt: string | null;
  createdAt: string;
};

export type VolunteerReportRow = {
  id: string;
  /** Il volontario **segnalato**. Di chi ha segnalato non c'è niente (AC1). */
  volunteer: { id: string; displayName: string | null };
  reason: string;
  text: string | null;
  status: ReportStatus;
  createdAt: string;
  handledAt: string | null;
  session: ModerationSession;
  note: ReportNote | null;
};

export type UserReportRow = {
  id: string;
  reportedBy: string | null;
  reason: string;
  text: string | null;
  status: ReportStatus;
  createdAt: string;
  handledAt: string | null;
  session: ModerationSession;
  note: ReportNote | null;
  bannable: boolean;
};

export type ModerationState = {
  threshold: number;
  volunteerReports: VolunteerReportRow[];
  userReports: UserReportRow[];
  flagged: { volunteerId: string; displayName: string | null; open: number }[];
  suspended: { id: string; displayName: string; suspendedAt: string | null }[];
  /** Chi fa parte della squadra: solo per chi coordina, per registrarne l'uscita. */
  team: { id: string; displayName: string; role: string }[];
  bans: {
    id: string;
    /** L'etichetta con cui compariva nel prodotto. Mai un numero di telefono. */
    label: { custom: string | null; number: number } | null;
    reason: string;
    createdAt: string;
    expiresAt: string | null;
  }[];
  /**
   * Chi sta guardando può anche **decidere**?
   *
   * Un supervisore legge le segnalazioni sui volontari — è il suo mestiere
   * (sezione 7quater) — ma sospensioni e blocchi li decide il coordinamento.
   * La schermata non inventa permessi: li chiede.
   */
  canModerate: boolean;
};

export function moderationState(options: ReadOptions = {}): Promise<ModerationState> {
  return request('/api/admin/moderation', options);
}

export function handleVolunteerReport(
  id: string,
  status: ReportStatus,
): Promise<{ status: ReportStatus }> {
  return request(`/api/admin/volunteer-reports/${id}/handle`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

export function handleUserReport(
  id: string,
  status: ReportStatus,
): Promise<{ status: ReportStatus }> {
  return request(`/api/admin/reports/${id}/handle`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

/**
 * Sospendere o riammettere.
 *
 * `sessionStillRunning` è la cosa che chi preme il pulsante deve sapere: la
 * conversazione in corso prosegue fino alla fine, perché il sistema non
 * interrompe mai una sessione attiva (vincolo 5).
 */
export function setSuspension(
  volunteerId: string,
  input: { suspended: boolean; reason: string },
): Promise<{ suspended: boolean; sessionStillRunning: boolean }> {
  return request(`/api/admin/volunteers/${volunteerId}/suspension`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * L'uscita di un volontario (sezione 7quinquies): irreversibile, cancella
 * notifiche push, numero per l'SMS e secondo fattore, toglie i turni futuri.
 */
export function exitVolunteer(
  volunteerId: string,
  body: { reason: string },
): Promise<{ exited: boolean; sessionStillRunning: boolean }> {
  return request(`/api/admin/volunteers/${volunteerId}/exit`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Si parte da una segnalazione: nessun numero di telefono passa di qui. */
export function banFromReport(input: {
  reportId: string;
  reason: string;
  days?: number;
}): Promise<{ status: string; sessionStillRunning: boolean }> {
  return request('/api/admin/bans', { method: 'POST', body: JSON.stringify(input) });
}

export function liftBan(id: string): Promise<{ status: string }> {
  return request(`/api/admin/bans/${id}`, { method: 'DELETE' });
}

/** "Segna come rivista in supervisione" (mock 3). */
export function markNoteReviewed(id: string): Promise<{ reviewedAt: string }> {
  return request(`/api/admin/notes/${id}/reviewed`, { method: 'POST' });
}

// ------------------------------------------------- amministrazione (fase 7)

/**
 * Le schermate di rendicontazione dell'ente.
 *
 * Tutte in **sola lettura**: "i report si generano dal registro immutabile,
 * mai da input manuali, e nessun ruolo può modificare o cancellare una sessione
 * registrata" (sezione 9quater). Qui non esiste una funzione che scriva un
 * numero, e non deve esistere.
 */

export type SessionRow = {
  id: string;
  channel: string;
  anonymous: boolean;
  requestedAt: string;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  outcome: string | null;
  /** `isFormer`: è uscito (sezione 7quinquies), le sue note restano. */
  volunteer: { id: string; displayName: string | null; isFormer: boolean } | null;
  note: {
    id: string;
    riskLevel: RiskLevel | null;
    referredExternally: boolean;
    reviewedAt: string | null;
    /** Che ci sia del testo, non il testo: quello si legge aprendo la scheda. */
    hasText: boolean;
  } | null;
};

export type SessionFilters = {
  from?: string;
  to?: string;
  channel?: string;
  risk?: string;
  unanswered?: boolean;
  page?: number;
};

export type SessionHistory = {
  from: string;
  to: string;
  timezone: string;
  page: number;
  hasMore: boolean;
  sessions: SessionRow[];
  canWrite: boolean;
};

function queryString(filters: Record<string, string | number | boolean | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function sessionHistory(
  filters: SessionFilters = {},
  options: ReadOptions = {},
): Promise<SessionHistory> {
  return request(`/api/admin/sessions${queryString(filters)}`, options);
}

export type SessionDetail = {
  session: SessionRow & { connectedSeconds: number };
  note: {
    id: string;
    riskLevel: RiskLevel | null;
    ageBand: string | null;
    topic: string | null;
    text: string | null;
    referredExternally: boolean;
    supervisorAlerted: string | null;
    reviewedAt: string | null;
    reviewedBy: string | null;
    createdAt: string;
  } | null;
  /** Che il supervisore sia stato allertato, non cosa si sono detti. */
  supervision: { supervisor: string | null; startedAt: string; endedAt: string | null } | null;
  canWrite: boolean;
};

export function sessionDetail(id: string, options: ReadOptions = {}): Promise<SessionDetail> {
  return request(`/api/admin/sessions/${id}`, options);
}

/**
 * L'avviso per le chiamate senza risposta (sezione 9quinquies), dalla parte
 * del coordinatore: `admin-alerts.ts` restituisce solo `unanswered_calls`,
 * mai gli altri tipi, e nessun `handledBy` — chi lo chiude è sempre il
 * fornitore, e il registro accessi dell'ente non lo nomina per nome.
 */
export type UnansweredAlert = {
  id: string;
  createdAt: string;
  handledAt: string | null;
  detail: { sessions: number; windowHours: number; threshold: number } | null;
};

export function unansweredAlerts(
  options: ReadOptions = {},
): Promise<{ alerts: UnansweredAlert[] }> {
  return request('/api/admin/alerts/unanswered', options);
}

export type UsageReport = {
  from: string;
  to: string;
  timezone: string;
  plan: {
    name: string;
    includedMinutes: number;
    capMinutes: number | null;
    inNetwork: boolean;
  };
  totals: {
    sessions: number;
    answered: number;
    unanswered: number;
    minutes: number;
    extraMinutes: number;
    averageSeconds: number;
  };
  byChannel: { channel: string; sessions: number; unanswered: number; minutes: number }[];
  months: { from: string; sessions: number; minutes: number }[];
  canWrite: boolean;
  /** Il canale email esiste ed è scrivibile da chi guarda: il pulsante "invia al coordinatore" compare solo allora. */
  canSendEmail: boolean;
};

export function usageReport(
  window: { from?: string; to?: string } = {},
  options: ReadOptions = {},
): Promise<UsageReport> {
  return request(`/api/admin/report${queryString(window)}`, options);
}

/**
 * "Invia al coordinatore" (sezione 9): il PDF del resoconto, per email. Su
 * richiesta esplicita — questo pulsante — mai un automatismo. Il destinatario
 * lo decide il server dalla sessione autenticata, non questa chiamata.
 */
export function sendReportEmail(window: { from?: string; to?: string } = {}): Promise<void> {
  return request('/api/admin/report/email', { method: 'POST', body: JSON.stringify(window) });
}

export type AccessEntry = {
  id: string | null;
  actorType: 'staff' | 'provider' | 'system';
  /** Nullo per il fornitore: nel registro dell'ente risponde il fornitore. */
  actor: string | null;
  action: string;
  category: 'reads' | 'decisions' | 'access';
  objectType: string | null;
  objectId: string | null;
  reason: string | null;
  occurredAt: string;
  firstAt: string;
  /** Quante righe identiche la vista raggruppata ha unito, nello stesso giorno. */
  occurrences: number;
};

export type AccessLog = {
  from: string;
  to: string;
  timezone: string;
  page: number;
  grouped: boolean;
  hasMore: boolean;
  entries: AccessEntry[];
  actors: { id: string; displayName: string }[];
  canWrite: boolean;
};

export type AccessFilters = {
  from?: string;
  to?: string;
  category?: string;
  actorId?: string;
  detailed?: boolean;
  page?: number;
};

export function accessLog(
  filters: AccessFilters = {},
  options: ReadOptions = {},
): Promise<AccessLog> {
  return request(`/api/admin/access-log${queryString(filters)}`, options);
}

/**
 * Scarica un export.
 *
 * Passa da `fetch` e non da un collegamento diretto perché la richiesta deve
 * portare il cookie di sessione e il token CSRF come tutte le altre, e perché
 * un errore deve poter diventare un messaggio invece di una pagina bianca.
 *
 * **Il file arriva in chiavi di traduzione, e si traduce qui.** Il server non
 * costruisce testo rivolto a nessuno (sezione 4), e la lingua è quella di chi
 * ha premuto il pulsante, non del server: intestazioni, canali, esiti,
 * valutazioni e azioni del registro escono come `admin.action_session_read` e
 * diventano "ha aperto una conversazione" qui.
 *
 * Non serve un analizzatore di CSV: le celle da tradurre sono **chiavi**, cioè
 * lettere minuscole, cifre, trattini bassi e un punto — non contengono mai una
 * virgola, un apice doppio o un a capo, e occupano la cella intera. Si
 * sostituiscono quindi per intero, e tutto il resto del documento — il testo
 * libero, che è l'unica cosa che può contenere un separatore — resta byte per
 * byte come il server l'ha scritto, virgolette e neutralizzazione delle
 * formule comprese (`docs/DECISIONI.md`, AJ5).
 */
export async function downloadCsv(
  path: string,
  translate: (key: string) => string,
): Promise<{ filename: string; content: string }> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: { ...(csrfToken() ? { 'x-csrf-token': csrfToken() as string } : {}) },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => undefined)) as ApiErrorBody | undefined;
    throw new ApiError(
      response.status,
      body?.error ?? { code: 'unexpected', messageKey: 'errors.unexpected' },
    );
  }

  const disposition = response.headers.get('content-disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'export.csv';
  const raw = await response.text();

  /**
   * Una cella che è **esattamente** una chiave, e nient'altro.
   *
   * `translate` restituisce la chiave stessa quando non c'è una traduzione: in
   * quel caso non si tocca niente, così una chiave nuova e non ancora tradotta
   * resta riconoscibile invece di sparire dietro una stringa vuota.
   */
  return {
    filename,
    content: raw.replaceAll(/"([a-z][a-z0-9_]*\.[a-z0-9_]+)"/g, (whole, key: string) => {
      const text = translate(key);
      return text === key ? whole : `"${text.replaceAll('"', '""')}"`;
    }),
  };
}

/**
 * Il PDF del resoconto: **binario**, non testo — niente chiavi da tradurre
 * dentro, perché il server compone già le etichette nella lingua dello staff
 * che ha premuto il pulsante (`report-pdf.ts`). Qui c'è solo da scaricarlo.
 */
export async function downloadPdf(path: string): Promise<{ filename: string; blob: Blob }> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: { ...(csrfToken() ? { 'x-csrf-token': csrfToken() as string } : {}) },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => undefined)) as ApiErrorBody | undefined;
    throw new ApiError(
      response.status,
      body?.error ?? { code: 'unexpected', messageKey: 'errors.unexpected' },
    );
  }

  const disposition = response.headers.get('content-disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'export.pdf';
  return { filename, blob: await response.blob() };
}

// ------------------------------------------------------ fornitore (fase 7)

export type PlatformTenantRow = {
  id: string;
  slug: string;
  name: string;
  integrated: boolean;
  plan: string;
  sessions: number;
  minutes: number;
  includedMinutes: number;
  paymentStatus: 'current' | 'due_soon' | 'overdue';
  isActive: boolean;
  newSessionsSuspended: boolean;
  networkId: string | null;
  livekitClusterId: string | null;
};

export function platformTenants(
  options: ReadOptions = {},
): Promise<{ month: string; tenants: PlatformTenantRow[] }> {
  return request('/api/superadmin/tenants', options);
}

/**
 * Un ente nuovo (mock 4, `s-new`).
 *
 * L'invito torna **una volta sola**: da qui in poi in tabella c'è solo l'hash
 * (AK3). Un'email parte in automatico al coordinatore invitato, ma
 * best-effort: il collegamento restituito qui resta il modo che non fallisce
 * mai, da consegnare a mano se serve.
 */
export type NewTenantInput = {
  name: string;
  slug: string;
  coordinatorEmail: string;
  plan: string;
  includedMinutes: number;
  countryCode: string;
  timezone: string;
  defaultLocale: string;
  siteUrl?: string;
};

export type TenantReadiness = {
  emergencyNumbers: boolean;
  crisisProtocol: boolean;
  openingHours: boolean;
  /** La nomina a responsabile del trattamento, con una copia firmata caricata (AN). */
  art28Signed: boolean;
  ready: boolean;
};

export type CreatedTenant = {
  tenant: {
    id: string;
    slug: string;
    siteKey: string;
    name: string;
    origin: string;
    isActive: boolean;
  };
  invite: { url: string; expiresAt: string; email: string };
  readiness: TenantReadiness;
};

export function createTenant(input: NewTenantInput): Promise<CreatedTenant> {
  return request('/api/superadmin/tenants', { method: 'POST', body: JSON.stringify(input) });
}

/**
 * Rifare l'invito del primo coordinatore (o di un altro ruolo) quando il
 * collegamento originale è andato perso o è scaduto (AK3, AM6): gli inviti
 * aperti dello stesso ruolo si chiudono, il nuovo si mostra una volta sola.
 */
export function reissueInvite(
  id: string,
  input: { email: string; role: 'volunteer' | 'supervisor' | 'coordinator'; reason: string },
): Promise<{ invite: { url: string; expiresAt: string; email: string } }> {
  return request(`/api/superadmin/tenants/${id}/invite`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Accendere o spegnere un ente. Non tocca nessuna conversazione in corso. */
/**
 * La cessazione di un ente: irreversibile, con motivazione e secondo fattore.
 * Fa partire la retention decennale delle copie firmate (BW3).
 */
export function ceaseTenant(
  id: string,
  input: { reason: string; code: string },
): Promise<{ ceasedAt: string; signedDocuments: number; liveSessions: number }> {
  return request(`/api/superadmin/tenants/${id}/cessation`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function setTenantActivation(
  id: string,
  input: { isActive: boolean; reason: string },
): Promise<{ isActive: boolean; readiness: TenantReadiness; liveSessions: number }> {
  return request(`/api/superadmin/tenants/${id}/activation`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Spostare un ente su un altro cluster media (sezione 7, mock 4 `s-clusters`).
 *
 * Vale solo per le nuove sessioni: quelle in corso restano dove sono finché
 * non finiscono da sole.
 */
export function setTenantCluster(
  id: string,
  input: { clusterId: string; reason: string },
): Promise<{
  livekitClusterId: string;
  cluster: { id: string; name: string };
  liveSessions: number;
}> {
  return request(`/api/superadmin/tenants/${id}/cluster`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Il costo mensile di un cluster: testo libero, `null` per "nessun costo impostato". */
export function setClusterCost(
  id: string,
  input: { monthlyCost: string | null; reason: string },
): Promise<{ id: string; monthlyCost: string | null }> {
  return request(`/api/superadmin/clusters/${id}/cost`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export type DomainStatus = 'pending' | 'verified' | 'unresolved';

export type DomainRow = {
  tenantId: string;
  tenant: string;
  slug: string;
  domain: string | null;
  /** Il record da creare, con il bersaglio che viene dalla configurazione. */
  cname: { name: string; type: 'CNAME'; value: string } | null;
  status: DomainStatus;
  checkedAt: string | null;
  unresolvedSince: string | null;
  certificateExpiresAt: string | null;
  isActive: boolean;
};

export function platformDomains(
  options: ReadOptions = {},
): Promise<{ edgeHost: string; domains: DomainRow[] }> {
  return request('/api/superadmin/domains', options);
}

export function setTenantDomain(
  id: string,
  input: { domain: string | null; reason: string },
): Promise<{ domain: string | null; status: DomainStatus | null }> {
  return request(`/api/superadmin/tenants/${id}/domain`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/** Autorizzare un sito per il widget dell'ente (BW6). */
export function addTenantOrigin(
  id: string,
  input: { origin: string; reason: string },
): Promise<{ id: string; origin: string }> {
  return request(`/api/superadmin/tenants/${id}/origins`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function removeTenantOrigin(
  id: string,
  originId: string,
  input: { reason: string },
): Promise<{ removed: boolean }> {
  return request(`/api/superadmin/tenants/${id}/origins/${originId}`, {
    method: 'DELETE',
    body: JSON.stringify(input),
  });
}

export function verifyTenantDomain(id: string): Promise<{
  /** Se la verifica è riuscita, non se il dominio risolve: sono due cose. */
  checked: boolean;
  status: DomainStatus;
  target: string | null;
  checkedAt: string | null;
  unresolvedSince: string | null;
  certificateExpiresAt: string | null;
}> {
  return request(`/api/superadmin/tenants/${id}/domain/verify`, { method: 'POST' });
}

export type PlatformTenantDetail = {
  readiness: TenantReadiness;
  /** I siti da cui il widget può aprire sessioni (BW6). */
  /** `expiresAt`: solo per un sito `localhost`, che scade da solo. */
  origins: { id: string; origin: string; isManaged: boolean; expiresAt: string | null }[];
  openInvites: {
    id: string;
    role: string;
    expiresAt: string;
    expired: boolean;
  }[];
  tenant: {
    id: string;
    slug: string;
    name: string;
    integrated: boolean;
    customDomain: string | null;
    plan: string;
    includedMinutes: number;
    capMinutes: number | null;
    networkId: string | null;
    paymentStatus: 'current' | 'due_soon' | 'overdue';
    subscriptionDueOn: string | null;
    isActive: boolean;
    newSessionsSuspended: boolean;
    /** Quando il rapporto è finito, per sempre (BW3). */
    ceasedAt: string | null;
    createdAt: string;
    timezone: string;
    cluster: { id: string; name: string } | null;
    domainStatus: DomainStatus | null;
    domainCheckedAt: string | null;
    domainUnresolvedSince: string | null;
    certificateExpiresAt: string | null;
  };
  usage: {
    sessions: number;
    minutes: number;
    participantMinutes: number;
    unanswered: number;
  };
  technical: {
    volunteers: number;
    liveSessions: number;
    unansweredWeek: number;
    otpMonth: number;
    lastWebhookAt: string | null;
  };
};

export function platformTenant(
  id: string,
  options: ReadOptions = {},
): Promise<PlatformTenantDetail> {
  return request(`/api/superadmin/tenants/${id}`, options);
}

/** Cambiare piano su un ente già acceso (sezione 9, clausola 5 del contratto). */
export function setTenantPlan(
  id: string,
  input: { plan: string; includedMinutes: number; reason: string },
): Promise<{ plan: string; includedMinutes: number }> {
  return request(`/api/superadmin/tenants/${id}/plan`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Lo stato amministrativo si imposta **a mano**: nessun pagamento nel prodotto. */
export function setTenantStatus(
  id: string,
  input: { paymentStatus?: string; newSessionsSuspended?: boolean; reason: string },
): Promise<{ paymentStatus: string; newSessionsSuspended: boolean; liveSessions: number }> {
  return request(`/api/superadmin/tenants/${id}/status`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Entrare in un ente per assistenza: 2FA e motivazione, sempre (sezione 9bis). */
export function impersonate(
  id: string,
  input: { reason: string; code: string },
): Promise<{ status: string; csrfToken: string; tenant: { slug: string; name: string } }> {
  return request(`/api/superadmin/tenants/${id}/impersonate`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function endImpersonation(): Promise<{ status: string }> {
  return request('/api/superadmin/impersonation/end', { method: 'POST' });
}

export type PlatformAlert = {
  id: string;
  kind: string;
  tenant: string | null;
  tenantId: string | null;
  detail: Record<string, string | number | boolean> | null;
  createdAt: string;
  handledAt: string | null;
  handledBy: string | null;
};

export function platformAlerts(
  state = 'open',
  options: ReadOptions = {},
): Promise<{ alerts: PlatformAlert[] }> {
  return request(`/api/superadmin/alerts?state=${state}`, options);
}

export function handleAlert(id: string): Promise<{ handledAt: string }> {
  return request(`/api/superadmin/alerts/${id}/handle`, { method: 'POST' });
}

export type GlobalBan = {
  id: string;
  /** Solo il prefisso e il suffisso dell'impronta: mai un numero, mai un hash intero. */
  hashPreview: string;
  reason: string;
  createdAt: string;
  expiresAt: string | null;
  createdBy: string | null;
};

export type BanCandidate = {
  banId: string;
  hashPreview: string;
  /** Da quanti enti diversi, non da quali. */
  tenants: number;
  lastAt: string;
};

export function blacklist(
  options: ReadOptions = {},
): Promise<{ bans: GlobalBan[]; candidates: BanCandidate[] }> {
  return request('/api/superadmin/blacklist', options);
}

export function addToBlacklist(input: {
  banId: string;
  reason: string;
  days?: number;
}): Promise<{ status: string }> {
  return request('/api/superadmin/blacklist', { method: 'POST', body: JSON.stringify(input) });
}

export function removeFromBlacklist(
  id: string,
  input: { reason: string },
): Promise<{ status: string }> {
  return request(`/api/superadmin/blacklist/${id}`, {
    method: 'DELETE',
    body: JSON.stringify(input),
  });
}

export type PlatformHealth = {
  live: { sessions: number; staffOnline: number; openAlerts: number; webhooksLastHour: number };
  clusters: {
    id: string;
    name: string;
    kind: string;
    region: string;
    isActive: boolean;
    fallbackClusterId: string | null;
    tenants: number;
    lastWebhookAt: string | null;
    /** Testo libero, scritto a mano dal fornitore. `null` = nessun costo impostato. */
    monthlyCost: string | null;
  }[];
  /**
   * Lo stato del job di retention, regola per regola.
   *
   * È la parte che rende non silenzioso un fallimento: una regola senza
   * `lastRunAt` non è mai girata, e va vista (sezione 7quinquies).
   */
  retention: {
    task: string;
    table: string;
    days: number;
    reason: string;
    lastRunAt: string | null;
    finishedAt: string | null;
    rows: number | null;
    error: string | null;
  }[];
};

export function platformHealth(options: ReadOptions = {}): Promise<PlatformHealth> {
  return request('/api/superadmin/health', options);
}

export type ImpersonationEntry = {
  id: string | null;
  tenant: string | null;
  tenantId: string | null;
  admin: string | null;
  action: string;
  reason: string | null;
  occurredAt: string;
};

export function impersonationLog(
  options: ReadOptions = {},
): Promise<{ entries: ImpersonationEntry[] }> {
  return request('/api/superadmin/impersonations', options);
}

export type PlatformNetwork = {
  id: string;
  name: string;
  plan: string;
  includedMinutes: number;
  maxTenants: number | null;
  usedMinutes: number;
  contact: { displayName: string; email: string } | null;
  tenants: {
    id: string;
    name: string;
    minutes: number;
    sessions: number;
    capMinutes: number | null;
  }[];
};

export function platformNetworks(
  options: ReadOptions = {},
): Promise<{ month: string; networks: PlatformNetwork[] }> {
  return request('/api/superadmin/networks', options);
}

// ------------------------------------------------ referente di rete (fase 7)

/**
 * Quello che il referente della rete vede, e nient'altro.
 *
 * Sessioni, minuti, durata media, chiamate senza risposta, turni scoperti per
 * ente. **Mai** debriefing, note, segnalazioni o utenti: non è un filtro di
 * interfaccia, è che quelle rotte non esistono per lui (vincolo 7).
 */
export type NetworkOverview = {
  network: { name: string; plan: string; includedMinutes: number; maxTenants: number | null };
  contact: { displayName: string };
  from: string;
  to: string;
  totals: {
    sessions: number;
    answered: number;
    unanswered: number;
    minutes: number;
    averageSeconds: number;
    activeTenants: number;
  };
  tenants: {
    id: string;
    name: string;
    sessions: number;
    unanswered: number;
    minutes: number;
    capMinutes: number | null;
    uncoveredWindows: number;
  }[];
  distribution: { hour: number; sessions: number }[];
};

export function networkOverview(
  window: { from?: string; to?: string } = {},
  options: ReadOptions = {},
): Promise<NetworkOverview> {
  return request(`/api/network/overview${queryString(window)}`, options);
}

// ------------------------------------------------------------ inviti (fase 8)

export type InviteView = {
  tenant: { name: string; slug: string };
  /** Mascherato: serve a riconoscere l'indirizzo, non a impararlo. */
  email: string;
  role: string;
  expiresAt: string;
};

/**
 * L'invito con cui il primo coordinatore entra (G4).
 *
 * Il token arriva dal **frammento** dell'indirizzo e riparte in un **header**,
 * mai nel percorso: un percorso finisce nel registro delle richieste dell'API,
 * e un invito letto da un log vale un accesso da coordinatore in un ente che
 * non è il proprio (`docs/DECISIONI.md`, AK3 e AM1).
 */
export function readInvite(token: string, options: ReadOptions = {}): Promise<InviteView> {
  return request('/api/staff/invite', {
    ...options,
    headers: { 'x-invite-token': token },
  });
}

export function acceptInvite(
  token: string,
  input: { name: string; displayName: string; password: string; trainingCompleted: boolean },
): Promise<{ status: string }> {
  return request('/api/staff/invite/accept', {
    method: 'POST',
    body: JSON.stringify({ ...input, token }),
  });
}

// ------------------------------------ il collegamento del referente tecnico (mock 5, o-tech)

export type DomainLinkView = {
  tenant: { name: string };
  cname: { name: string; type: 'CNAME'; value: string };
  status: 'pending' | 'verified' | 'unresolved';
  checkedAt: string | null;
  certificateExpiresAt: string | null;
  expiresAt: string;
};

export type DomainLinkVerifyResult = {
  checked: boolean;
  status: 'pending' | 'verified' | 'unresolved';
  target: string | null;
};

/**
 * Le istruzioni CNAME dietro un token, senza sessione.
 *
 * Come per `readInvite`, il token riparte come **header**, mai nel percorso:
 * un percorso finisce nel registro delle richieste dell'API
 * (`docs/DECISIONI.md`, AZ4).
 */
export function readDomainLink(token: string, options: ReadOptions = {}): Promise<DomainLinkView> {
  return request('/api/public/domain-link', {
    ...options,
    headers: { 'x-domain-link-token': token },
  });
}

export function verifyDomainLink(token: string): Promise<DomainLinkVerifyResult> {
  return request('/api/public/domain-link/verify', {
    method: 'POST',
    headers: { 'x-domain-link-token': token },
  });
}

export type DomainLinkStatus = {
  active: boolean;
  expiresAt: string | null;
  domainConfigured: boolean;
};

/** Lo stato del collegamento corrente, dalla console del coordinatore. */
export function domainLinkStatus(): Promise<DomainLinkStatus> {
  return request('/api/admin/domain-link');
}

export type IssuedDomainLink = {
  url: string;
  expiresAt: string;
  cname: { name: string; type: 'CNAME'; value: string };
};

/** Genera un nuovo collegamento, revocando quello precedente. */
export function generateDomainLink(): Promise<IssuedDomainLink> {
  return request('/api/admin/domain-link', { method: 'POST' });
}

// ------------------------------------------------- l'aspetto del widget (sezione 4)

/**
 * La palette derivata dall'unico colore che l'ente sceglie.
 *
 * Arriva dal server e **non si ricalcola qui**: è la stessa derivazione che
 * risponde al widget, e una seconda copia nella console vorrebbe dire una
 * schermata che promette un colore e un sito che ne mostra un altro.
 */
export type WidgetPalette = {
  accent: string;
  accentText: string;
  accentHover: string;
  accentSubtle: string;
  accentOnSurface: string;
  corrected: boolean;
  dark: {
    accent: string;
    accentText: string;
    accentHover: string;
    accentSubtle: string;
    accentOnSurface: string;
  };
};

/**
 * Cosa la piattaforma userà davvero, e **quanto** legge.
 *
 * I rapporti di contrasto vengono con la palette perché la schermata li deve
 * scrivere a parole: un quadratino colorato non dice a nessuno se quel colore
 * raggiunge la soglia, e a chi non vede non dice proprio niente.
 */
export type PaletteView = {
  palette: WidgetPalette;
  corrected: boolean;
  contrast: { light: number; dark: number; requestedOnWhite: number };
  /** Il colore usato è diverso da quello scelto? Due risposte per i due temi. */
  adjusted: { light: boolean; dark: boolean };
};

export type WidgetLayout = 'compact' | 'extended' | 'button_only';

export type EmergencyContact = { number: string; labels: Record<string, string> };

export type BrandingState = PaletteView & {
  /** Falso per il fornitore in assistenza: guarda e non tocca (sezione 9bis). */
  editable: boolean;
  name: string;
  logoUrl: string | null;
  accentColor: string;
  layout: WidgetLayout;
  layouts: WidgetLayout[];
  channels: string[];
  availableChannels: string[];
  defaultLocale: string;
  /** Tutte le lingue che la piattaforma sa scrivere: il menu della scelta. */
  supportedLocales: string[];
  locales: string[];
  /** Ciò che l'ente ha scritto, per lingua. Vuoto = usa il testo della piattaforma. */
  texts: Record<string, Record<string, string>>;
  /** Il testo che il visitatore legge se l'ente non scrive niente. */
  defaults: Record<string, Record<string, string>>;
  /** Le sei caselle e il loro limite: la console non lo decide. */
  fields: { key: string; maxLength: number }[];
  /** Le frasi che l'ente non cambia, già tradotte: l'anteprima le mostra. */
  safety: Record<string, string[]>;
  emergency: {
    fixed: { number: string; labelKey: string | null }[];
    own: EmergencyContact[];
    max: number;
    labelMaxLength: number;
  };
};

export type BrandingPatch = {
  accentColor?: string;
  layout?: WidgetLayout;
  channels?: string[];
  texts?: Record<string, Record<string, string>>;
  emergencyContacts?: EmergencyContact[];
  defaultLocale?: string;
  autoDetectLocale?: boolean;
};

export function branding(options: ReadOptions = {}): Promise<BrandingState> {
  return request('/api/admin/branding', options);
}

/** L'anteprima di un colore che non è ancora stato salvato. */
export function brandingPalette(accent: string, options: ReadOptions = {}): Promise<PaletteView> {
  return request(`/api/admin/branding/palette?accent=${encodeURIComponent(accent)}`, options);
}

export function saveBranding(input: BrandingPatch): Promise<BrandingState & { changed: string[] }> {
  return request('/api/admin/branding', { method: 'PATCH', body: JSON.stringify(input) });
}

// ---------------------------------------------------------------- documenti

/** I due documenti "da firmare" (AN). */
export const SIGNABLE_DOCUMENT_TYPES = ['service_contract', 'dpa_appointment'] as const;
export type SignableDocumentType = (typeof SIGNABLE_DOCUMENT_TYPES)[number];

/** I tre documenti "da conservare", generati al volo e mai caricati. */
export const READ_ONLY_DOCUMENT_TYPES = [
  'sub_processors',
  'privacy_notice',
  'security_measures',
] as const;
export type ReadOnlyDocumentType = (typeof READ_ONLY_DOCUMENT_TYPES)[number];

export type DocumentsState = {
  editable: boolean;
  signable: {
    type: SignableDocumentType;
    category: 'signed';
    signed: { uploadedAt: string; sizeBytes: number } | null;
  }[];
  readOnly: { type: ReadOnlyDocumentType; category: 'reference' }[];
};

export function documents(options: ReadOptions = {}): Promise<DocumentsState> {
  return request('/api/admin/documents', options);
}

/**
 * Il PDF generato dall'ente: sia i due "da firmare" sia i tre "da conservare".
 *
 * Non passa da `request`: la risposta non è JSON, è un file da scaricare, e
 * il browser lo apre da sé partendo dalla stessa sessione con cui è già
 * autenticato (un `GET` non porta il token CSRF, che protegge solo le
 * scritture).
 */
export function documentPdfUrl(type: SignableDocumentType | ReadOnlyDocumentType): string {
  return `/api/admin/documents/${type}/pdf`;
}

/**
 * Il caricamento della copia firmata: `multipart/form-data`, non JSON.
 *
 * Bypassa `request()` di proposito — quella funzione forza sempre
 * `content-type: application/json` quando il corpo non è vuoto, e un file
 * caricato con quel `content-type` non verrebbe letto come multipart né dal
 * browser né dal server.
 */
export async function uploadDocumentSignature(
  type: SignableDocumentType,
  file: File,
): Promise<{ type: SignableDocumentType; signed: { uploadedAt: string; sizeBytes: number } }> {
  const token = csrfToken();
  const body = new FormData();
  body.append('file', file);

  const response = await fetch(`/api/admin/documents/${type}/signature`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: token ? { 'x-csrf-token': token } : {},
    body,
  });

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => undefined)) as ApiErrorBody | undefined;
    throw new ApiError(
      response.status,
      errorBody?.error ?? { code: 'unexpected', messageKey: 'errors.unexpected' },
    );
  }

  return (await response.json()) as {
    type: SignableDocumentType;
    signed: { uploadedAt: string; sizeBytes: number };
  };
}

/**
 * Il logo, caricato come file (`docs/DECISIONI.md`, AZ11).
 *
 * Non passa da `request()`: quella funzione mette sempre
 * `content-type: application/json` quando c'è un corpo, e un `FormData` ha
 * bisogno del proprio — con il confine (`boundary`) che solo il browser sa
 * scrivere. Impostarlo a mano vorrebbe dire ricostruirlo a mano.
 */
export async function uploadLogo(file: File, options: ReadOptions = {}): Promise<BrandingState> {
  const token = csrfToken();
  const body = new FormData();
  body.append('logo', file);

  const response = await fetch('/api/admin/branding/logo', {
    method: 'POST',
    credentials: 'same-origin',
    signal: options.signal,
    headers: { ...(token ? { 'x-csrf-token': token } : {}) },
    body,
  });

  if (!response.ok) {
    const parsed = (await response.json().catch(() => undefined)) as ApiErrorBody | undefined;
    throw new ApiError(
      response.status,
      parsed?.error ?? { code: 'unexpected', messageKey: 'errors.unexpected' },
    );
  }
  return (await response.json()) as BrandingState;
}

export function removeLogo(options: ReadOptions = {}): Promise<BrandingState> {
  return request('/api/admin/branding/logo', { method: 'DELETE', signal: options.signal });
}
